import { map } from 'rxjs/operators';
import { __decorate, __metadata } from 'tslib';
import { BaseModel, Property } from '@red/core';
import { AUTH_STRATEGY, ROLE, Me } from '@red/data';
import { ERROR_CODE, BaseServiceClient } from '@red/common';
import { BehaviorSubject } from 'rxjs';

class AppConfigService {
    constructor(http, config) {
        this._config = config;
        this._http = http;
    }
    static create(http, config) {
        return new AppConfigService(http, config);
    }
    load(configObj, envConfigType) {
        this._config.update(configObj);
        if (envConfigType) {
            return this._http
                .get(envConfigType)
                .pipe(map((response) => {
                this._config.update(response.body);
                return this._config;
            }))
                .toPromise();
        }
        return Promise.resolve(this._config);
    }
    getConfig() {
        return this._config;
    }
}

class BaseConfig {
    constructor(data) {
        this._conf = {};
        if (data) {
            this.update(data);
        }
    }
    get(key) {
        const path = key.split('.');
        const value = path.reduce((acc, step) => {
            if (!acc || !acc[step]) {
                return null;
            }
            acc = acc[step];
            return acc;
        }, this._conf);
        return value || null;
    }
    update(data) {
        this._merge(this._conf, data);
    }
    _isObject(item) {
        return (item && typeof item === 'object' && !Array.isArray(item));
    }
    _merge(target, ...sources) {
        if (!sources.length) {
            return target;
        }
        const source = sources.shift();
        if (this._isObject(target) && this._isObject(source)) {
            for (const key in source) {
                if (this._isObject(source[key])) {
                    if (!target[key]) {
                        Object.assign(target, { [key]: {} });
                    }
                    this._merge(target[key], source[key]);
                }
                else {
                    Object.assign(target, { [key]: source[key] });
                }
            }
        }
        return this._merge(target, ...sources);
    }
}

class BankIdAuthRequest extends BaseModel {
}
__decorate([
    Property(),
    __metadata("design:type", String)
], BankIdAuthRequest.prototype, "identifier", void 0);
__decorate([
    Property({ type: AUTH_STRATEGY }),
    __metadata("design:type", String)
], BankIdAuthRequest.prototype, "strategy", void 0);
__decorate([
    Property({ type: ROLE }),
    __metadata("design:type", String)
], BankIdAuthRequest.prototype, "scope", void 0);

class BankIdAuthResponse extends BaseModel {
}
__decorate([
    Property(),
    __metadata("design:type", String)
], BankIdAuthResponse.prototype, "authReference", void 0);
__decorate([
    Property(),
    __metadata("design:type", String)
], BankIdAuthResponse.prototype, "autostartToken", void 0);

class BankIdCollectResponse extends BaseModel {
}
__decorate([
    Property(),
    __metadata("design:type", String)
], BankIdCollectResponse.prototype, "sessionToken", void 0);
__decorate([
    Property(),
    __metadata("design:type", String)
], BankIdCollectResponse.prototype, "status", void 0);

class ErrorMessageMapper {
    translate(error, fallbackMessage) {
        if (typeof error === 'string') {
            return null;
        }
        let err;
        try {
            err = typeof error.message === 'string' ? JSON.parse(error.message) : error;
        }
        catch (e) {
            err = error;
        }
        const errorCode = parseInt(err.errorCode || err.status || -1, 10);
        if (this._isKnownError(errorCode)) {
            return this._translationForCode(errorCode);
        }
        return fallbackMessage || err.message || null;
    }
    compileErrorMessages() {
        this._messages = {};
        this._messages[`err_${ERROR_CODE.UNAUTHORIZED}`] = 'Unauthorized';
        this._messages[`err_${ERROR_CODE.FORBIDDEN}`] = 'Forbidden';
        this._messages[`err_${ERROR_CODE.NOT_FOUND}`] = 'Not Found';
        this._messages[`err_${ERROR_CODE.INTERNAL_SERVER_ERROR}`] = 'Internal server error';
        this._messages[`err_${ERROR_CODE.GENERAL_ERROR}`] = 'Unspecified error';
        this._messages[`err_${ERROR_CODE.MISSING_BANK_DETAILS}`] = 'You need to enter you bank details. Please enter theses under settings.';
        this._messages[`err_${ERROR_CODE.MISSING_BANK_DETAILS_SALARY}`] = 'You need to enter you bank details. Please enter theses under settings.';
        this._messages[`err_${ERROR_CODE.REGISTRATION_INVALID_FIELD}`] = 'You have entered a invalid email or phone number, please check them and try again.';
    }
    _isKnownError(errorCode) {
        return errorCode in ERROR_CODE;
    }
    _translationForCode(errorCode) {
        const errStr = `err_${errorCode}`;
        if (this._messages[errStr]) {
            return this._messages[errStr];
        }
        return null;
    }
}

var LIST_STATUS;
(function (LIST_STATUS) {
    LIST_STATUS[LIST_STATUS["LOADING"] = 0] = "LOADING";
    LIST_STATUS[LIST_STATUS["NO_RESULT"] = 1] = "NO_RESULT";
    LIST_STATUS[LIST_STATUS["DONE"] = 2] = "DONE";
})(LIST_STATUS || (LIST_STATUS = {}));
class ListDataSource {
    constructor(query) {
        this._query = query;
        this._items = new BehaviorSubject([]);
    }
    get complete() { return this._complete; }
    get hasResults() { return this._items.getValue().length > 0; }
    get items() { return this._items; }
    flush() {
        this._filter = null;
        this._items.next([]);
    }
    next(filter) {
        return this._next(filter);
    }
    _next(filter) {
        const f = filter || this._filter;
        const offset = (parseInt(f.params()['offset'], 10) || 0);
        return this._query(f)
            .pipe(map((resp) => {
            this._items.next(this._items.getValue().concat(resp.results));
            this._filter = resp.filter;
            if (offset + resp.length < resp.total) {
                this._complete = false;
                this._filter.offset((parseInt(this._filter.params()['offset'], 10) || 0) + resp.length);
            }
            else {
                this._complete = true;
            }
            return resp;
        }));
    }
}

class MathHelper {
    static roundToDigit(num, scale) {
        if (!('' + num).includes('e')) {
            return +(Math.round(Number(`${num}e+${scale}`)) + `e-${scale}`);
        }
        else {
            const arr = (`${num}`).split('e');
            let sig = '';
            if (+arr[1] + scale > 0) {
                sig = '+';
            }
            return +(Math.round(+Number(`${arr[0]}e${sig}${(+arr[1] + scale)}`)) + `e-${scale}`);
        }
    }
}

class QueryFilter {
    constructor(params) {
        this._params = {};
        if (params) {
            Object.keys(params).forEach(filterKey => this._populate(filterKey, params[filterKey]));
        }
    }
    equal(key, value) {
        this._params[key] = value;
        return this;
    }
    get(key) {
        return this._params[key] || null;
    }
    length(length) {
        if (typeof length === 'string') {
            length = parseInt(length, 10);
        }
        if (typeof length === 'number') {
            this._length = length;
        }
        return this;
    }
    offset(offset) {
        if (typeof offset === 'string') {
            offset = parseInt(offset, 10);
        }
        if (typeof offset === 'number') {
            this._offset = offset;
        }
        return this;
    }
    params() {
        const pagination = {};
        if (!isNaN(this._length)) {
            pagination['length'] = this._length;
        }
        if (!isNaN(this._offset)) {
            pagination['offset'] = this._offset;
        }
        return Object.assign({}, this._params, pagination);
    }
    _populate(key, value) {
        switch (key) {
            case 'length':
                this.length(value);
                break;
            case 'offset':
                this.offset(value);
                break;
            default:
                this.equal(key, value);
        }
    }
}

class QueryResponse {
    constructor(results, filter, total) {
        this._filter = filter;
        this._results = results;
        this._total = total;
    }
    get filter() { return this._filter; }
    get length() { return this._results && this._results.length || 0; }
    get results() { return this._results; }
    get total() { return this._total; }
}

class WebServiceClient extends BaseServiceClient {
    constructor(transport, appConfig) {
        super(transport);
        this._appConfig = appConfig;
    }
    _getBaseUrl() { return this._appConfig.get('baseApiUrl') || ''; }
}

class AuthorizationServiceClient extends WebServiceClient {
    static create(transport, config) {
        return new AuthorizationServiceClient(transport, config);
    }
    authenticate(credentials, headers) {
        const options = headers ? { headers } : {};
        return this._post(`${this._getBaseUrl()}/api/authenticate`, credentials, options, BankIdAuthResponse);
    }
    authenticateGoogle(credentials, headers) {
        const options = headers ? { headers } : {};
        return this._post(`${this._getBaseUrl()}/api/google/authenticate`, credentials, options, BankIdCollectResponse);
    }
    collect(authReference) {
        return this._get(`${this._getBaseUrl()}/api/authenticate/${authReference}`, {}, BankIdCollectResponse);
    }
    me() {
        return this._get(`${this._getBaseUrl()}/api/me`, {}, Me);
    }
    setActiveCompany(companyId) {
        return this._put(`${this._getBaseUrl()}/api/authenticate`, { companyId }, {}, BankIdCollectResponse);
    }
}

class Session extends BehaviorSubject {
    get relations() { return this._relations; }
    get state() { return this._state; }
    get user() { return this._user; }
    hasRelation(relation) {
        return this._relations.some((rel) => rel.role === relation.role && rel.company.id === relation.company.id);
    }
    setRelations(relations) {
        this._relations = relations;
        this.next(this);
        return this;
    }
    setState(state) {
        this._state = state;
        this.next(this);
        return this;
    }
    setUser(user) {
        this._user = user;
        this.next(this);
        return this;
    }
    destroy() {
        this._user = null;
        this._state = null;
        this._relations = [];
    }
}

class StoredSession extends Session {
    constructor(cookieKey, cookieService) {
        super(null);
        this._sessionKey = cookieKey;
        this._storage = cookieService;
        this._load();
    }
    get valid() { return this.state; }
    logout() {
        super.destroy();
        this._updateSession();
    }
    setState(state) {
        super.setState(state);
        this._updateSession();
        return this;
    }
    _load() {
        const currentSession = this._storage.get(this._sessionKey);
        if (currentSession) {
            const { state } = JSON.parse(atob(currentSession));
            this.setState(state || null);
        }
        return currentSession;
    }
    _updateSession() {
        this._storage.put(this._sessionKey, btoa(JSON.stringify({ 'state': this.state })));
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AppConfigService, AuthorizationServiceClient, BankIdAuthRequest, BankIdAuthResponse, BankIdCollectResponse, BaseConfig, ErrorMessageMapper, LIST_STATUS, ListDataSource, MathHelper, QueryFilter, QueryResponse, StoredSession, WebServiceClient };
